import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from '@mui/material';
import {useCreditsBalance} from '../../context/CreditsBalanceContext';
import {ACTION_CREDIT_SUCCESS_NOTE} from '../../helper/actionCreditEstimate';
import {formatCreditCount} from '../../helper/creditBalanceModel';
import {getEnrichmentActionLabel} from './EnrichmentGrid.helpers';
import {AI_BATCH_MAX_COMPANIES, fetchWorkspaceQuote, priceGridRun} from './gridPaidRun';

const credits = (count) => `${formatCreditCount(count)} ${count === 1 ? 'credit' : 'credits'}`;

function confirmLabel(review, saved, price) {
  if (saved) return `Confirm up to ${price.total ?? '…'} credits`;
  if (review.kind !== 'data' && review.companyIds.length === 1) return 'Continue to charge';
  return `Confirm up to ${formatCreditCount(price.total)} credits`;
}

/** One confirmation for every paid grid run: the backend quote when a saved search exists, else the upper bound. */
export default function SearchAiEnrichmentReview({review, onClose, onConfirm, onQuoteError}) {
  const creditState = useCreditsBalance();
  const [state, setState] = useState({status: 'loading', quote: null, error: null});
  const [retry, setRetry] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const confirmingRef = useRef(false);

  useEffect(() => {
    if (!review) return undefined;
    if (!review.workspaceId) {
      setState({status: 'ready', quote: null, error: null});
      return undefined;
    }
    let current = true;
    setState({status: 'loading', quote: null, error: null});
    fetchWorkspaceQuote(review).then(({quote, error}) => {
      if (current) {
        setState(quote ? {status: 'ready', quote, error: null} : {status: 'error', quote: null, error});
        if (!quote) onQuoteError?.(error);
      }
    });
    return () => { current = false; };
  }, [review, retry, onQuoteError]);

  const price = review ? priceGridRun({review, quote: state.quote, credits: creditState}) : null;
  // Only a saved-search run waits for a quote; the others are priced on the client.
  const ready = !review?.workspaceId || state.status === 'ready';

  const confirm = useCallback(async () => {
    // A ref, not state: two clicks inside one React batch must not both submit.
    if (confirmingRef.current || !ready || !review || price.blocked) return;
    if (review.workspaceId && !(Date.parse(state.quote?.expires_at) > Date.now())) {
      setState({status: 'error', quote: null, error: 'This quote expired. Load a new quote before charging.'});
      onQuoteError?.('This quote expired. Load a new quote before charging.');
      return;
    }
    confirmingRef.current = true;
    setConfirming(true);
    try {
      const outcome = await onConfirm(review, state.quote);
      if (outcome?.requote) setRetry((value) => value + 1);
      else onClose();
    } finally {
      confirmingRef.current = false;
      setConfirming(false);
    }
  }, [onClose, onConfirm, onQuoteError, price, ready, review, state]);

  if (!review) return null;
  const saved = Boolean(review.workspaceId);
  const count = review.companyIds.length;
  return (
    <Dialog open onClose={confirming ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="search-ai-review-title">
      <DialogTitle id="search-ai-review-title">{review.kind === 'data' ? 'Review Data Enrichment' : 'Review AI enrichment'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{mb: 2}}>
          {count} {count === 1 ? 'company' : 'companies'} · {review.kind === 'data' ? getEnrichmentActionLabel(review.actionId) : review.model}
        </Typography>
        {saved && (
          <>
            {state.status === 'loading' && <Box role="status" sx={{display: 'flex', gap: 1, alignItems: 'center'}}><CircularProgress size={18} />Loading the current credit quote…</Box>}
            {state.status === 'error' && <Alert severity="error">{state.error}</Alert>}
            {state.status === 'ready' && (
              <Alert severity="info">
                Up to {state.quote.max_credits} credits ({state.quote.credits_per_company} per completed company).
                Results will remain in this saved search and each company’s history.
              </Alert>
            )}
          </>
        )}
        {!saved && review.kind === 'data' && (
          <Alert severity="info">
            Up to {credits(price.total)} ({price.perUnit} per {review.actionId === 'verify_email' ? 'email' : 'company'}). This is an upper bound. {ACTION_CREDIT_SUCCESS_NOTE}
          </Alert>
        )}
        {!saved && review.kind !== 'data' && (
          <Alert severity="warning">
            This Search column lasts only for the current view. Paid results remain in each company’s history.
            Save this search first if you want results together in Saved Search.
            <Box sx={{mt: 1}}>
              <Button size="small" href={`/company/${encodeURIComponent(review.companyIds[0])}`} target="_blank" rel="noopener noreferrer">
                Open company history
              </Button>
            </Box>
          </Alert>
        )}
        {!saved && review.kind !== 'data' && count > 1 && (
          <Alert severity="info" sx={{mt: 1}}>Up to {credits(price.total)} ({price.perUnit} per completed company).</Alert>
        )}
        {price.overLimit && (
          <Alert severity="error" sx={{mt: 1}}>
            AI enrichment runs on at most {AI_BATCH_MAX_COMPANIES} companies at a time. Select {AI_BATCH_MAX_COMPANIES} or fewer.
          </Alert>
        )}
        {!price.balanceResolved && <Box role="status" sx={{mt: 1}}>Checking your credit balance…</Box>}
        {price.shortfall > 0 && (
          <Alert severity="error" sx={{mt: 1}}>
            You have {credits(price.available)} available, {credits(price.shortfall)} short.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirming}>Cancel</Button>
        {state.status === 'error' && saved && <Button onClick={() => setRetry((value) => value + 1)}>Retry quote</Button>}
        <Button onClick={confirm} variant="contained" disabled={confirming || !ready || price.blocked}>
          {confirmLabel(review, saved, price)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
